// Friendly bets between viewers, kept in this browser: who picked which cats, the game mode,
// what a point is worth, and the score so far. Scored from the official results of live races,
// the same races the race history keeps (see history.ts). Rules: register entry bet-scoring-rules.
import { CAT_IDS, type CatId, type RaceSnapshot } from '../race/types';
import type { RaceRecord } from './history';

/** h2h: the best-placed cat among the bet's cats takes the race's point. wins: a point for each outright race win. */
export type BetMode = 'h2h' | 'wins';
export type BetStatus = 'active' | 'paused' | 'settled';

export interface Participant { id: string; name: string; cats: CatId[] }

/** One counted race, as the bet saw it. */
export interface BetRace {
  at: number;
  /** Places of the bet's cats in that race; dsq cats have place null. */
  places: { id: CatId; place: number | null }[];
  /** Participant who got the point, or null. */
  scorer: string | null;
}

export interface Bet {
  id: string;
  title: string;
  createdAt: number;
  mode: BetMode;
  euroPerPoint: number;
  /** Number of races the bet lasts, or null for continuous: until one of the bet's cats wins a race. */
  races: number | null;
  participants: Participant[];
  status: BetStatus;
  /** When the bet was created or last resumed. Only races that started after this count. */
  activeSince: number;
  settledAt: number | null;
  /** Races counted so far. */
  counted: number;
  points: Record<string, number>;
  /** The latest counted races, oldest first (capped at MAX_LOG). */
  log: BetRace[];
}

export interface BetDraft {
  title: string;
  mode: BetMode;
  euroPerPoint: number;
  races: number | null;
  participants: { name: string; cats: CatId[] }[];
}

/** What a race did to one bet, for the ticker. */
export interface BetOutcome { betId: string; title: string; scorer: string | null; scorerCat: CatId | null; place: number | null; settled: boolean }

export const MAX_RACES = 99;
export const MAX_EURO = 10_000;
export const MAX_PARTICIPANTS = 6;
const MAX_LOG = 200;
const STORAGE_KEY = 'kd-bets';

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** Which participant has this cat in the bet. */
export function ownerOf(bet: Pick<Bet, 'participants'>, cat: CatId): Participant | undefined {
  return bet.participants.find((p) => p.cats.includes(cat));
}

/** Plain-language problems with a draft; empty when the bet can be placed. */
export function draftProblems(d: BetDraft): string[] {
  const out: string[] = [];
  const named = d.participants.filter((p) => p.name.trim());
  if (d.participants.length < 2) out.push('Add at least two participants.');
  if (named.length < d.participants.length) out.push('Give every participant a name.');
  const names = named.map((p) => p.name.trim().toLowerCase());
  if (new Set(names).size < names.length) out.push('Use a different name for each participant.');
  if (d.participants.some((p) => p.cats.length === 0)) out.push('Pick at least one cat for every participant.');
  const cats = d.participants.flatMap((p) => p.cats);
  if (new Set(cats).size < cats.length) out.push('Each cat can belong to only one participant.');
  if (!Number.isFinite(d.euroPerPoint) || d.euroPerPoint < 0 || d.euroPerPoint > MAX_EURO) out.push(`A point must be worth 0–${MAX_EURO} €.`);
  if (d.races !== null && (!Number.isInteger(d.races) || d.races < 1 || d.races > MAX_RACES)) out.push(`A bet lasts 1–${MAX_RACES} races.`);
  return out;
}

/** "Nemo vs Tyyne"-style title from the cats, used when the bet has no name. */
export function autoTitle(d: Pick<BetDraft, 'participants'>, short: (id: CatId) => string): string {
  const sides = d.participants.filter((p) => p.cats.length).map((p) => p.cats.map(short).join(' & '));
  return sides.length >= 2 ? sides.join(' vs ') : 'New bet';
}

export function createBet(d: BetDraft, now: number, short: (id: CatId) => string): Bet {
  const participants = d.participants.map((p) => ({ id: newId(), name: p.name.trim(), cats: CAT_IDS.filter((c) => p.cats.includes(c)) }));
  return {
    id: newId(), title: d.title.trim() || autoTitle(d, short), createdAt: now,
    mode: d.mode, euroPerPoint: Math.round(d.euroPerPoint * 100) / 100, races: d.races,
    participants, status: 'active', activeSince: now, settledAt: null,
    counted: 0, points: Object.fromEntries(participants.map((p) => [p.id, 0])), log: [],
  };
}

type Placed = { id: CatId; place: number };

/** The race's finishers that were not disqualified, best first. */
function placed(results: readonly { id: CatId; place: number; dsq: boolean }[]): Placed[] {
  return results.filter((r) => !r.dsq).sort((a, b) => a.place - b.place);
}

/** The cat that earns the point in this race under the bet's rules, or null. */
export function scoringCat(bet: Pick<Bet, 'mode' | 'races' | 'participants'>, results: readonly { id: CatId; place: number; dsq: boolean }[]): Placed | null {
  const ranked = placed(results);
  // Continuous bets are decided by an outright win in either mode.
  if (bet.mode === 'wins' || bet.races === null) {
    const w = ranked[0];
    return w && ownerOf(bet, w.id) ? w : null;
  }
  return ranked.find((r) => ownerOf(bet, r.id)) ?? null;
}

/** Applies one live race's official results to every bet it counts for. */
export function applyRace(bets: readonly Bet[], race: Pick<RaceRecord, 'at' | 'results' | 'startedAt'>): { bets: Bet[]; outcomes: BetOutcome[] } {
  const outcomes: BetOutcome[] = [];
  const startedAt = race.startedAt ?? race.at;
  const next = bets.map((bet) => {
    if (bet.status !== 'active' || bet.activeSince > startedAt) return bet;
    const win = scoringCat(bet, race.results);
    const scorer = win ? ownerOf(bet, win.id)!.id : null;
    const counted = bet.counted + 1;
    const settled = bet.races === null ? scorer !== null : counted >= bet.races;
    const betCats = bet.participants.flatMap((p) => p.cats);
    const places = race.results.filter((r) => betCats.includes(r.id)).sort((a, b) => a.place - b.place)
      .map((r) => ({ id: r.id, place: r.dsq ? null : r.place }));
    outcomes.push({ betId: bet.id, title: bet.title, scorer, scorerCat: win?.id ?? null, place: win?.place ?? null, settled });
    return {
      ...bet, counted,
      points: scorer ? { ...bet.points, [scorer]: (bet.points[scorer] ?? 0) + 1 } : bet.points,
      log: [...bet.log, { at: race.at, places, scorer }].slice(-MAX_LOG),
      status: settled ? 'settled' as const : bet.status,
      settledAt: settled ? race.at : null,
    };
  });
  return { bets: next, outcomes };
}

export function pauseBet(bets: readonly Bet[], id: string): Bet[] {
  return bets.map((b) => (b.id === id && b.status === 'active' ? { ...b, status: 'paused' as const } : b));
}

/** Resuming starts counting again from the next race. */
export function resumeBet(bets: readonly Bet[], id: string, now: number): Bet[] {
  return bets.map((b) => (b.id === id && b.status === 'paused' ? { ...b, status: 'active' as const, activeSince: now } : b));
}

export function deleteBet(bets: readonly Bet[], id: string): Bet[] {
  return bets.filter((b) => b.id !== id);
}

/**
 * Money each participant is up or down. For every point between two players,
 * the one behind pays the one ahead the point's value, so the balances add up to zero.
 */
export function balances(bet: Bet): Record<string, number> {
  const n = bet.participants.length;
  const total = bet.participants.reduce((s, p) => s + (bet.points[p.id] ?? 0), 0);
  return Object.fromEntries(bet.participants.map((p) => [p.id, Math.round((n * (bet.points[p.id] ?? 0) - total) * bet.euroPerPoint * 100) / 100]));
}

/** Who pays whom to settle up, in as few payments as the balances allow. Amounts in euros. */
export function payments(bet: Bet): { from: string; to: string; amount: number }[] {
  const bal = balances(bet);
  const cents = (v: number) => Math.round(v * 100);
  const owe = bet.participants.map((p) => ({ id: p.id, c: -cents(bal[p.id]!) })).filter((x) => x.c > 0).sort((a, b) => b.c - a.c);
  const get = bet.participants.map((p) => ({ id: p.id, c: cents(bal[p.id]!) })).filter((x) => x.c > 0).sort((a, b) => b.c - a.c);
  const out: { from: string; to: string; amount: number }[] = [];
  let i = 0; let j = 0;
  while (i < owe.length && j < get.length) {
    const c = Math.min(owe[i]!.c, get[j]!.c);
    out.push({ from: owe[i]!.id, to: get[j]!.id, amount: c / 100 });
    owe[i]!.c -= c; get[j]!.c -= c;
    if (owe[i]!.c === 0) i++;
    if (get[j]!.c === 0) j++;
  }
  return out;
}

/** Participants with the most points; empty while nobody has any. */
export function leaders(bet: Bet): Participant[] {
  const top = Math.max(0, ...bet.participants.map((p) => bet.points[p.id] ?? 0));
  return top === 0 ? [] : bet.participants.filter((p) => (bet.points[p.id] ?? 0) === top);
}

/** The bet's best-placed cat in the race on screen right now, with its owner. */
export function currentBest(bet: Bet, snap: RaceSnapshot): { owner: Participant; cat: CatId; place: number } | null {
  let best: { owner: Participant; cat: CatId; place: number } | null = null;
  for (const c of snap.cats) {
    const owner = ownerOf(bet, c.id);
    const r = snap.results?.find((x) => x.id === c.id);
    if (r?.dsq) continue;
    const place = r?.place ?? c.place;
    if (owner && (!best || place < best.place)) best = { owner, cat: c.id, place };
  }
  return best;
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;
const defaultStorage = (): Storage | undefined => { try { return globalThis.localStorage; } catch { return undefined; } };

function isBet(b: unknown): b is Bet {
  const x = b as Bet;
  return !!x && typeof x.id === 'string' && typeof x.title === 'string' && (x.mode === 'h2h' || x.mode === 'wins')
    && typeof x.euroPerPoint === 'number' && (x.races === null || typeof x.races === 'number')
    && Array.isArray(x.participants) && x.participants.every((p) => p && typeof p.id === 'string' && typeof p.name === 'string' && Array.isArray(p.cats) && p.cats.every((c) => CAT_IDS.includes(c)))
    && ['active', 'paused', 'settled'].includes(x.status) && typeof x.activeSince === 'number'
    && typeof x.counted === 'number' && !!x.points && typeof x.points === 'object' && Array.isArray(x.log);
}

export function loadBets(storage: Storage | undefined = defaultStorage()): Bet[] {
  try {
    const raw = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter(isBet) : [];
  } catch { return []; }
}

export function saveBets(bets: readonly Bet[], storage: Storage | undefined = defaultStorage()): void {
  try { storage?.setItem(STORAGE_KEY, JSON.stringify(bets)); } catch { /* storage full or blocked: keep them for this visit */ }
}
